import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { find, isEmpty, map, filter } from 'lodash';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';

import uuid from 'uuid/v1';
import * as atlasOps from '../../../api/Atlas/atlas-operations';
import { ATLAS_ELEMENTS, ATLAS_CHILDS, ATLAS_PROP_TYPES } from '../AtlasAtoms/atlas-elements';
import TelemetryConnections from '../../../api/TelemetryConnections/TelemetryConnections';

import getDefaultValuesForForms from '../../../modules/get-default-values-for-forms';
import MapsConfigSharedModal from '../MapsConfigSharedModal';
import VideoFormElement from '../VideoFormElement';
import ColorPicker from '../../components/ColorPicker';
import Loading from '../../components/Loading';
import { renderButton } from '../../components/MaterialMUIComponent';

const NAV_BAR_HEIGHT = 56;
const HEADER_HEIGHT = 54; // When it is all in the same line
const TITLE_HEIGHT = 100;
const BUTTON_HEIGHT = 70;
const VERTICAL_HEIGHT = NAV_BAR_HEIGHT + HEADER_HEIGHT + TITLE_HEIGHT + BUTTON_HEIGHT;
const ColorFormElement = ({ className, defaultValue, formName, propHumanValue }) => {
  const [selectedColor, changeSelectedColor] = React.useState(defaultValue || '#fff');

  React.useEffect(() => changeSelectedColor(defaultValue || '#fff'), [defaultValue]);
  return (
    <Form.Group className={className} as={Row} key={formName}>
      <Form.Label column xs={8}>
        {propHumanValue}
      </Form.Label>
      <Col xs={4}>
        <ColorPicker
          alpha={1}
          color={selectedColor}
          disableAlpha
          updateColor={colorStyle => changeSelectedColor(colorStyle.hex)}
        />
      </Col>
      <Form.Control
        name={formName}
        value={selectedColor}
        onChange={() => {}}
        style={{ visibility: 'hidden', height: '0px' }}
      />
    </Form.Group>
  );
};

ColorFormElement.propTypes = {
  className: PropTypes.string.isRequired,
  defaultValue: PropTypes.string.isRequired,
  formName: PropTypes.string.isRequired,
  propHumanValue: PropTypes.string.isRequired
};

const FormElements = ({
  elementProps,
  elementValue,
  elementHumanValue,
  isMain,
  elementIndex,
  defaultValues
}) => {
  const className = isMain ? '' : 'pl-5';
  return (
    <div role="button" onKeyPress={() => {}} tabIndex={0}>
      {isMain && (
        <Form.Group>
          {elementIndex !== 0 && <hr />}
          <h3>
            <strong>{elementHumanValue}</strong>
          </h3>
        </Form.Group>
      )}
      {map(elementProps, ({ name, type, humanValue: propHumanValue }, index) => {
        const formName = `${elementValue}-${index}-prop-${name}`;
        const { defaultValue } = find(defaultValues, ['formName', formName]) || '';
        if (type === ATLAS_PROP_TYPES.STRING) {
          return (
            <Form.Group className={className} key={formName}>
              <Form.Label>{propHumanValue}</Form.Label>
              <Form.Control
                name={formName}
                placeholder="Oh, The Places You'll Go!"
                defaultValue={defaultValue}
              />
            </Form.Group>
          );
        }
        if (type === ATLAS_PROP_TYPES.TEXTAREA) {
          return (
            <Form.Group className={className} key={uuid()}>
              <Form.Label>{propHumanValue}</Form.Label>
              <Form.Control
                as="textarea"
                name={formName}
                placeholder="Congratulations! Today is your day. You're off to Great Places! You're
                off and away!"
                defaultValue={defaultValue}
              />
            </Form.Group>
          );
        }
        if (type === ATLAS_PROP_TYPES.COLOR) {
          return (
            <ColorFormElement
              className={className}
              defaultValue={defaultValue}
              formName={formName}
              propHumanValue={propHumanValue}
            />
          );
        }
        if (type === ATLAS_PROP_TYPES.NODE) {
          const [selectedNode, changeSelectedNode] = React.useState(defaultValue);
          const nodeProperties = find(ATLAS_ELEMENTS.properties, ['value', selectedNode]) || '';
          const { props: nodeProps, value: nodeValue, humanValue: nodeHumanValue } = nodeProperties;
          const childNodeValue = `${formName}-${nodeValue}`;
          return (
            <Fragment key={formName}>
              <Form.Group className={className}>
                <Form.Label>{propHumanValue}</Form.Label>
                <Form.Control
                  as="select"
                  name={formName}
                  value={selectedNode}
                  onChange={event => changeSelectedNode(event.target.value)}
                >
                  <option value="">-</option>
                  {map(ATLAS_CHILDS, ({ humanValue: componentName, value: componentValue }) => {
                    return (
                      <option value={componentValue} key={componentValue}>
                        {componentName}
                      </option>
                    );
                  })}
                </Form.Control>
              </Form.Group>
              {!isEmpty(selectedNode) && (
                <FormElements
                  elementProps={nodeProps}
                  elementValue={childNodeValue}
                  elementHumanValue={nodeHumanValue}
                  defaultValues={defaultValues}
                />
              )}
            </Fragment>
          );
        }
        return '';
      })}
    </div>
  );
};

FormElements.defaultProps = {
  isMain: false,
  elementIndex: 0
};

FormElements.propTypes = {
  isMain: PropTypes.bool,
  elementIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  elementProps: PropTypes.array.isRequired,
  elementValue: PropTypes.string.isRequired,
  elementHumanValue: PropTypes.string.isRequired,
  defaultValues: PropTypes.array.isRequired
};

const AtlasContentStep = ({
  atlas,
  gmsConnections,
  isAnnotationsAtlas,
  loading,
  selectedStructure,
  showButtons,
  changeShowButtons,
  handlePreviewProps,
  changeUserConfiguration,
  sharedConfiguration
}) => {
  let labelConexion = '';
  const [openModalSharedConfig, setOpenModalSharedConfig] = React.useState(false);
  const filterConnections = filter(gmsConnections, element => {
    if (selectedStructure[0] === 'CreateDataset') {
      labelConexion = 'Server Connection for DataSet';
      return element.serverType === 'IGP';
    }
    labelConexion = 'Server Connection';
    return true;
  });
  const handleModalUserShared = settings => {
    setOpenModalSharedConfig(false);
    changeUserConfiguration(settings);
  };
  if (loading) return <Loading />;
  return (
    <Row>
      <Col xs={12} sm={6}>
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control
            name="name"
            defaultValue={atlasOps.getName(atlas)}
            placeholder="Oh, The Places You'll Go!"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            name="description"
            defaultValue={atlasOps.getDescription(atlas)}
            placeholder="Congratulations! Today is your day. You're off to Great Places! You're off and away!"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Image Preview</Form.Label>
          <Form.Control
            type="text"
            name="imageUrl"
            defaultValue={atlasOps.getImageURL(atlas)}
            placeholder="An image to use as preview"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>{labelConexion}</Form.Label>
          <Form.Control
            as="select"
            name="gmsConnection"
            defaultValue={atlasOps.getGMSConnection(atlas)}
          >
            <option value="">-</option>
            {map(filterConnections, ({ _id, name }) => {
              return (
                <option value={_id} key={_id}>
                  {name}
                </option>
              );
            })}
          </Form.Control>
        </Form.Group>
        {selectedStructure[0] === 'CreateDataset' && (
          <Form.Group>
            <Form.Label>Server Connection for training</Form.Label>
            <Form.Control
              as="select"
              name="gmsConnection2"
              defaultValue={atlasOps.getGMSConnection2(atlas)}
            >
              <option value="">-</option>
              {map(gmsConnections, ({ _id, name }) => {
                return (
                  <option value={_id} key={_id}>
                    {name}
                  </option>
                );
              })}
            </Form.Control>
          </Form.Group>
        )}
        <Form.Group as={Row}>
          {renderButton({
            contentButton: 'Configure shared maps',
            onClick: () => setOpenModalSharedConfig(true)
          })}
          <MapsConfigSharedModal
            sharedConfiguration={sharedConfiguration}
            atlas={atlas}
            openModalSharedConfig={openModalSharedConfig}
            handleModalUserShared={handleModalUserShared}
            isAtlas
          />
        </Form.Group>
        <Form.Group>
          <Form.Check
            type="checkbox"
            inline
            id="showButtons"
            name="showButtons"
            label="Show Buttons"
            checked={showButtons}
            onChange={changeShowButtons}
          />
        </Form.Group>
      </Col>
      <Col xs={12} sm={6} style={{ height: `calc(100vh - ${VERTICAL_HEIGHT}px)` }}>
        <div
          style={{
            height: '100%',
            width: '100%',
            overflow: 'hidden scroll'
          }}
        >
          {map(selectedStructure, (element, elementIndex) => {
            const elementProperties = find(ATLAS_ELEMENTS.properties, ['value', element]);
            const { props, value, humanValue } = elementProperties;
            const defaultValues = getDefaultValuesForForms({ atlas, element, elementIndex });
            return (
              <FormElements
                key={`${value}-prop`}
                elementProps={props}
                elementValue={value}
                elementHumanValue={humanValue}
                defaultValues={defaultValues}
                elementIndex={elementIndex}
                isMain
              />
            );
          })}
          <VideoFormElement
            atlas={atlas}
            isAnnotationsAtlas={isAnnotationsAtlas}
            selectedStructure={selectedStructure}
          />
        </div>
        <Button variant="outline-success" className="float-right mt-4" type="submit">
          {isEmpty(atlasOps.getId(atlas)) ? 'Add Atlas' : 'Save Atlas'}
        </Button>
        <Button
          variant="outline-primary"
          className="float-right mt-4 mr-4"
          onClick={event => handlePreviewProps(event.target.form)}
        >
          Preview
        </Button>
      </Col>
    </Row>
  );
};

AtlasContentStep.defaultProps = {
  gmsConnections: []
};

AtlasContentStep.propTypes = {
  atlas: PropTypes.object.isRequired,
  changeShowButtons: PropTypes.func.isRequired,
  changeUserConfiguration: PropTypes.func.isRequired,
  gmsConnections: PropTypes.array,
  handlePreviewProps: PropTypes.func.isRequired,
  isAnnotationsAtlas: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  selectedStructure: PropTypes.array.isRequired,
  sharedConfiguration: PropTypes.object.isRequired,
  showButtons: PropTypes.bool.isRequired
};

export default withTracker(() => {
  const gmsConnectionsSub = Meteor.isClient && Meteor.subscribe('gmsConnections');
  return {
    loading: !gmsConnectionsSub.ready(),
    gmsConnections: TelemetryConnections.find().fetch()
  };
})(AtlasContentStep);
